import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Button } from "../../ui";
import { cn } from "../../ui/utils/cn";
import { StatusChip } from "../contribute/statusMeta";
import {
  useGetAdminContributionsQuery,
  useGetAdminContributionQuery,
  useUpdateAdminContributionMutation,
  type ContributionStatus,
} from "../../services/rtkApi/contributeApi";

// Admin "Contributions" report (role-gated, /admin/contributions). The full report
// for each idea: the member's original words, the whole intake conversation (with
// voice transcripts), the confirmed summary, and the LLM build brief the admin can
// hand straight to a coding agent. Walk each idea through its workflow; shipping
// awards the contributor their props.

const WORKFLOW: ContributionStatus[] = ["submitted", "triaged", "accepted", "building", "shipped", "declined"];
const FILTERS: { key: string; label: string }[] = [
  { key: "", label: "all" },
  { key: "submitted", label: "submitted" },
  { key: "triaged", label: "reviewing" },
  { key: "accepted", label: "accepted" },
  { key: "building", label: "building" },
  { key: "shipped", label: "shipped" },
  { key: "declined", label: "archived" },
];

const FIELD =
  "w-full border-2 border-ink bg-background px-2 py-1.5 text-sm rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

function fmt(iso?: string): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return "—";
  }
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <div className="text-[11px] font-bold uppercase tracking-wide text-ink/50">{title}</div>
      {children}
    </div>
  );
}

function Detail({ id }: { id: string }) {
  const { data: c, isLoading } = useGetAdminContributionQuery(id);
  const [update, { isLoading: saving }] = useUpdateAdminContributionMutation();

  const [status, setStatus] = useState<ContributionStatus>("submitted");
  const [adminNotes, setAdminNotes] = useState("");
  const [featureTitle, setFeatureTitle] = useState("");
  const [featureUrl, setFeatureUrl] = useState("");

  useEffect(() => {
    if (c) {
      setStatus(c.status);
      setAdminNotes(c.adminNotes || "");
      setFeatureTitle(c.shipped?.featureTitle || "");
      setFeatureUrl(c.shipped?.featureUrl || "");
    }
  }, [c]);

  if (isLoading || !c) return <div className="text-sm text-ink/50 p-4">loading…</div>;

  const willShip = status === "shipped" && c.status !== "shipped";

  async function save() {
    try {
      await update({
        id,
        status,
        adminNotes,
        shipped: { featureTitle: featureTitle.trim(), featureUrl: featureUrl.trim() },
      }).unwrap();
      toast.success(willShip ? "shipped — contributor credited 🎉" : "saved");
    } catch (e: unknown) {
      toast.error((e as { data?: { error?: string } })?.data?.error || "couldn't save");
    }
  }

  function copyBrief() {
    const b = c?.brief;
    if (!b) return;
    const text = [
      `# ${b.title}`,
      `Category: ${b.category} · Feasibility: ${b.feasibility}`,
      "",
      `## Summary\n${b.summary}`,
      `## User value\n${b.userValue}`,
      `## Build brief\n${b.buildBrief}`,
      b.suggestedAreas.length ? `## Suggested areas\n- ${b.suggestedAreas.join("\n- ")}` : "",
      b.openQuestions.length ? `## Open questions\n- ${b.openQuestions.join("\n- ")}` : "",
      `## Original member pitch\n${c?.rawPitch || ""}`,
    ]
      .filter(Boolean)
      .join("\n\n");
    navigator.clipboard?.writeText(text).then(
      () => toast.success("brief copied — hand it to a Claude agent"),
      () => toast.error("couldn't copy")
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="text-lg font-bold break-words">{c.summary?.title || c.brief?.title || "(untitled)"}</div>
          <div className="text-xs text-ink/60">
            by <span className="font-semibold">{c.authorUsername}</span> · {fmt(c.timestamps?.submittedAt)}
            {c.voiceUsed && " · 🎙 voiced"}
          </div>
        </div>
        <StatusChip status={c.status} />
      </div>

      {/* the build brief — the deliverable for "tell a claude agent to create it" */}
      {c.brief && (
        <div className="border-brutal border-ink rounded-sm bg-lemon/15 p-3 space-y-2">
          <div className="flex items-center justify-between">
            <div className="text-[11px] font-bold uppercase tracking-wide text-ink/60">build brief</div>
            <Button size="sm" variant="outline" onClick={copyBrief}>
              copy brief
            </Button>
          </div>
          <div className="text-xs flex flex-wrap gap-x-3 gap-y-1 text-ink/70">
            <span>📂 {c.brief.category}</span>
            <span>🛠 {c.brief.feasibility}</span>
            {c.brief.suggestedAreas.length > 0 && <span>↪ {c.brief.suggestedAreas.join(", ")}</span>}
          </div>
          <div className="text-sm whitespace-pre-wrap break-words">{c.brief.buildBrief}</div>
          {c.brief.feasibilityNote && <div className="text-xs text-ink/60 italic">{c.brief.feasibilityNote}</div>}
          {c.brief.openQuestions.length > 0 && (
            <div className="text-xs">
              <span className="font-semibold">Open questions:</span>
              <ul className="list-disc ml-5">
                {c.brief.openQuestions.map((q, i) => (
                  <li key={i}>{q}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      <Section title="member's original pitch">
        <div className="text-sm whitespace-pre-wrap break-words border-2 border-ink/30 rounded-sm bg-paper p-2">
          {c.rawPitch || "—"}
        </div>
      </Section>

      <Section title="confirmed summary">
        <div className="text-sm border-2 border-ink/30 rounded-sm bg-paper p-2">
          <div className="font-semibold">{c.summary?.title}</div>
          <div className="whitespace-pre-wrap break-words">{c.summary?.pitch}</div>
        </div>
      </Section>

      <Section title={`full conversation (${c.chat.length} turns · fit: ${c.fitNote})`}>
        <div className="space-y-1.5 max-h-72 overflow-y-auto border-2 border-ink/20 rounded-sm p-2 bg-paper">
          {c.chat.map((m, i) => (
            <div key={i} className={cn("text-sm", m.role === "assistant" ? "text-ink/60" : "text-ink")}>
              <span className="font-bold">{m.role === "assistant" ? "Desk: " : "Member: "}</span>
              {m.voice && <span className="text-[10px] text-ink/40">[🎙 voice] </span>}
              <span className="whitespace-pre-wrap break-words">{m.content}</span>
              {m.voice?.rawTranscript && m.voice.rawTranscript !== m.content && (
                <span className="block text-[10px] text-ink/40 ml-2">raw: {m.voice.rawTranscript}</span>
              )}
            </div>
          ))}
        </div>
      </Section>

      {/* workflow controls */}
      <div className="border-brutal border-ink rounded-sm bg-paper p-3 space-y-2">
        <div className="text-[11px] font-bold uppercase tracking-wide text-ink/60">manage</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <label className="text-xs space-y-1">
            <span className="text-ink/60">status</span>
            <select className={FIELD} value={status} onChange={(e) => setStatus(e.target.value as ContributionStatus)}>
              {WORKFLOW.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </label>
          <label className="text-xs space-y-1">
            <span className="text-ink/60">shipped feature title</span>
            <input className={FIELD} value={featureTitle} onChange={(e) => setFeatureTitle(e.target.value)} placeholder="optional" />
          </label>
        </div>
        <label className="text-xs space-y-1 block">
          <span className="text-ink/60">shipped feature URL</span>
          <input className={FIELD} value={featureUrl} onChange={(e) => setFeatureUrl(e.target.value)} placeholder="/some-feature" />
        </label>
        <label className="text-xs space-y-1 block">
          <span className="text-ink/60">admin notes</span>
          <textarea className={cn(FIELD, "resize-none")} rows={2} value={adminNotes} onChange={(e) => setAdminNotes(e.target.value)} />
        </label>

        {willShip && (
          <div className="text-xs text-ink/70 bg-mint/20 border-2 border-ink rounded-sm p-2">
            Marking this shipped will credit <b>{c.authorUsername}</b> (+100 respect, Contributor badge, notification).
          </div>
        )}
        {c.credit?.awarded && (
          <div className="text-xs text-ink/60">
            credited: +{c.credit.respect} respect{c.credit.badgeGranted ? " · badge granted" : ""} · {fmt(c.credit.awardedAt)}
          </div>
        )}

        <Button size="sm" onClick={save} loading={saving}>
          save
        </Button>
      </div>
    </div>
  );
}

export default function ContributionsAdminPage() {
  const [filter, setFilter] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const { data, isLoading } = useGetAdminContributionsQuery(filter || undefined);
  const list = data?.contributions || [];

  return (
    <div className="max-w-6xl mx-auto w-full px-4 py-6 md:pb-16">
      <h1 className="text-2xl font-bold mb-1">Contributions</h1>
      <p className="text-sm text-ink/60 mb-4">Member-pitched ideas. Open one for the full report + build brief.</p>

      <div className="flex flex-wrap gap-2 mb-4">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={cn(
              "px-2.5 py-1 text-xs font-bold border-2 border-ink rounded-sm",
              filter === f.key ? "bg-mint" : "bg-paper hover:bg-accent"
            )}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[minmax(0,340px)_1fr] gap-4">
        {/* list */}
        <div className="space-y-2">
          {isLoading && <div className="text-sm text-ink/50">loading…</div>}
          {!isLoading && list.length === 0 && <div className="text-sm text-ink/50">no contributions yet.</div>}
          {list.map((c) => (
            <button
              key={c.id}
              onClick={() => setSelectedId(c.id)}
              className={cn(
                "w-full text-left border-2 border-ink rounded-sm p-2.5 bg-paper hover:bg-accent transition-colors",
                selectedId === c.id && "bg-mint/30 shadow-brutal-sm"
              )}
            >
              <div className="flex items-start justify-between gap-2">
                <span className="font-bold text-sm break-words">{c.title}</span>
                <StatusChip status={c.status} />
              </div>
              <div className="text-[11px] text-ink/50 mt-0.5">
                {c.authorUsername} · {c.category}
                {c.voiceUsed && " · 🎙"}
              </div>
            </button>
          ))}
        </div>

        {/* detail */}
        <div className="border-brutal border-ink rounded-sm bg-paper shadow-brutal-md p-4 min-h-[300px]">
          {selectedId ? (
            <Detail id={selectedId} />
          ) : (
            <div className="text-sm text-ink/40 h-full flex items-center justify-center">select a contribution to review</div>
          )}
        </div>
      </div>
    </div>
  );
}
